import React, { useState, useEffect } from 'react';
import { Download, Users, Star } from 'lucide-react';
import Navbar from '../components/Navbar';

interface StatsData {
  date: string;
  totalRequests: number;
  uniqueIps: number;
  uniqueIpsNoBot: number;
}

const Home: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/stats/today');
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('获取统计数据失败:', error);
      }
    };
    
    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const reviews = [
    {
      name: '张xx',
      role: '某创业公司创始人',
      content: '我本来以为是另外一个codex，没想到体验完全不同，100个agent同时跑实验，真的很棒，AI时代，快速找到’真理‘真的很重要',
    },
    {
      name: '李xx',
      role: '某10年程序员',
      content: '可以切换任意模型，特别适合我这种天天做模型测评和分享的，赞！',
    },
    {
      name: '王xx',
      role: '某产业集团部门长',
      content: '数据都在本地，不用担心泄露，大量燃烧token的我终于不用担心了，我隐隐约约看到了一个时代确确实实在发生了',
    },
    {
      name: '赵xx',
      role: '某量化基金研究院',
      content: '循环研究帮我自动化了很多重复工作，原来神用神经网络做价格建模离我如此近，我有一些沮丧，同时也有一些兴奋，ai时代，加油！',
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <Navbar />

      {/* ======== Hero Section ======== */}
      <header className="relative pt-44 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          {/* 大面积柔和光晕 */}
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-[radial-gradient(ellipse_at_center,_rgba(139,92,246,0.18)_0%,_transparent_70%)]" />
          <div className="absolute top-20 -left-20 w-[400px] h-[400px] bg-[radial-gradient(circle,_rgba(59,130,246,0.12)_0%,_transparent_70%)]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6 text-slate-800">
            一句话构建N个模型，让他们自我迭代
          </h1>

          <p className="text-lg sm:text-xl text-slate-500 mb-14">
            深度、并行、稳定
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a
              href="https://bb-1storage.oss-cn-hangzhou.aliyuncs.com/downloads/teegal-setup.exe"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-all duration-300 shadow-lg shadow-slate-500/20 hover:shadow-slate-500/30 hover:scale-105"
            >
              <Download size={18} />
              <span>Windows 版</span>
            </a>
            <a
              href="https://bb-1storage.oss-cn-hangzhou.aliyuncs.com/downloads/Teegal-mac.dmg"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-all duration-300 shadow-lg shadow-slate-500/20 hover:shadow-slate-500/30 hover:scale-105"
            >
              <Download size={18} />
              <span>macOS 版</span>
            </a>
          </div>

          {stats && stats.totalRequests > 0 && (
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <Users size={16} className="text-violet-500" />
              <span>
                今日 <strong className="text-violet-600 font-semibold">{(stats.totalRequests + 1).toLocaleString()}</strong> 次访问
              </span>
            </div>
          )}
        </div>
      </header>

      {/* ======== 四大特性概览 ======== */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-3 gap-4">
            {[
              { num: '100个', label: '最高并发Agent', color: 'from-violet-500 to-purple-600' },
              { num: '300h', label: '可连续运行', color: 'from-emerald-500 to-teal-600' },
              { num: '200次', label: '不间断循环', color: 'from-orange-500 to-amber-600' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100">
                <div className={`text-3xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-1`}>
                  {item.num}
                </div>
                <div className="text-sm text-slate-500">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 深度并行 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">深度并行</h2>
          <p className="text-slate-500 mb-8 leading-relaxed max-w-2xl mx-auto">
            优秀的生产力是串行叠加并行，多维度同时展开。Agent 之间可以独立工作、相互协作，将复杂任务拆解为并行子任务同时执行。
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: '100 并发', desc: '同时运行 100 个 Agent，各自处理不同任务' },
              { title: '300+ 小时', desc: '最长连续运行超过 300 小时不中断' },
              { title: '10x 效率', desc: '相比串行处理，整体效率提升 10 倍' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 700+ 模型接入 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">700+ 模型测评数据</h2>
          <p className="text-slate-500 mb-8 leading-relaxed max-w-2xl mx-auto">
            动态算法优化Token成本。支持 GPT-5.6、GLM-5.3、DeepSeek-V4、Kimi K3、Qwen3.8 等国内外主流大语言模型，以及视觉、语音、代码专用模型，深度耦合火山引擎与阿里云的云能力，根据任务自动选择最合适的模型。
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['GPT-5.6', 'GLM-5.3', 'GLM-5.3-Flash', 'GLM-5.2', 'DeepSeek-V4', 'Kimi K3', 'Qwen3.8', 'Gemini 3.6'].map((model) => (
              <div key={model} className="px-4 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl text-center text-sm font-medium text-blue-700 border border-blue-100">
                {model}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 云端漫步 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">云端漫步</h2>
          <p className="text-slate-500 mb-8 leading-relaxed max-w-2xl mx-auto">
            接入超大规模云提供商的全栈计算能力，让你的需求在超大规模云上漫步。你的每一次需求，都可能是一次全球性的算力计算。在凭证中托管云账号，Agent 即可 SSH 直连云端，自动完成实例构建与计算。
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
            {['火山云', '阿里云', 'AWS', 'Azure', 'Google Cloud'].map((cloud) => (
              <div key={cloud} className="px-4 py-3 bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl text-center text-sm font-medium text-sky-700 border border-sky-100">
                {cloud}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: '全栈云能力', desc: '计算、存储、网络、GPU 实例，云端资源一键编排' },
              { title: 'SSH 直连', desc: 'Agent 通过托管凭证直连云端，自动构建实例执行任务' },
              { title: '全球算力', desc: '跨区域调度全球数据中心，就近供给最优算力' },
              { title: '凭证安全托管', desc: '云账号加密保存在本地，调用全程可审计' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-gradient-to-br from-sky-50 to-white border border-sky-100 shadow-sm text-left">
                <h3 className="font-semibold text-slate-800 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 本地计算 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">本地计算</h2>
          <p className="text-slate-500 mb-8 leading-relaxed max-w-2xl mx-auto">
            数据保留在本地，敏感信息不出境。支持 Ollama、LM Studio、llama、SGlang、vllm 等本地模型部署方案，企业级安全合规。
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: '数据隐私', desc: '所有计算在本地完成，数据不上传云端' },
              { title: 'Llama 支持', desc: '一键接入本地 GGuf 模型，零配置使用' },
              { title: 'API 本地调用', desc: '本地 API 服务，延迟低、响应快' },
              { title: '企业级安全', desc: '符合企业数据安全规范，可审计可追溯' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm text-left">
                <h3 className="font-semibold text-slate-800 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 循环研究 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">递归循环</h2>
          <p className="text-slate-500 mb-8 leading-relaxed max-w-2xl mx-auto">
            用循环研究来加速业务。Agent 自主探索、验证、优化，持续迭代直到找到最优解。不需要人工干预，24 小时自动运转。覆盖 30+ 数据中心，涵盖 H200、H100、A100、A10、T4 等主流卡系，算力按需调度。
          </p>

          {/* 算力规模 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto mb-8">
            {['H200', 'H100', 'A100', 'A10 / T4'].map((card) => (
              <div key={card} className="px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl text-center text-sm font-semibold text-orange-700 border border-orange-100">
                {card}
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-400 mb-8">30+ 数据中心 · 多卡系混合调度 · 弹性供给</p>
          
          {/* 循环图示 */}
          <div className="relative max-w-md mx-auto mb-8">
            <svg viewBox="0 0 300 160" className="w-full h-auto">
              {/* 定义动画 */}
              <defs>
                <linearGradient id="flowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f97316" stopOpacity="0" />
                  <stop offset="50%" stopColor="#f97316" stopOpacity="1" />
                  <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                </linearGradient>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#f97316" />
                </marker>
              </defs>

              {/* 循环箭头背景 */}
              <path
                d="M 30 80 Q 30 30 150 30 Q 270 30 270 80 Q 270 130 150 130 Q 30 130 30 80"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="3"
                strokeDasharray="8 4"
              />

              {/* 流动动画路径 - 使用CSS动画 */}
              <path
                className="flow-path"
                d="M 30 80 Q 30 30 150 30 Q 270 30 270 80 Q 270 130 150 130 Q 30 130 30 80"
                fill="none"
                stroke="#f97316"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="20 200"
              />

              {/* 流动的点 */}
              <circle className="flow-dot" r="4" fill="#f97316">
                <animateMotion
                  dur="3s"
                  repeatCount="indefinite"
                  path="M 30 80 Q 30 30 150 30 Q 270 30 270 80 Q 270 130 150 130 Q 30 130 30 80"
                />
              </circle>

              {/* 节点 */}
              {/* 探索 */}
              <circle cx="30" cy="80" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="30" y="76" textAnchor="middle" className="text-xs font-medium" fill="#f97316">探索</text>
              <text x="30" y="88" textAnchor="middle" className="text-[9px]" fill="#64748b">Explore</text>

              {/* 验证 */}
              <circle cx="150" cy="30" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="150" y="26" textAnchor="middle" className="text-xs font-medium" fill="#f97316">验证</text>
              <text x="150" y="38" textAnchor="middle" className="text-[9px]" fill="#64748b">Verify</text>

              {/* 优化 */}
              <circle cx="270" cy="80" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="270" y="76" textAnchor="middle" className="text-xs font-medium" fill="#f97316">优化</text>
              <text x="270" y="88" textAnchor="middle" className="text-[9px]" fill="#64748b">Optimize</text>

              {/* 迭代 */}
              <circle cx="150" cy="130" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="150" y="126" textAnchor="middle" className="text-xs font-medium" fill="#f97316">迭代</text>
              <text x="150" y="138" textAnchor="middle" className="text-[9px]" fill="#64748b">Iterate</text>

              {/* 中心文字 */}
              <text x="150" y="78" textAnchor="middle" className="text-[10px]" fill="#94a3b8">持续优化</text>
              <text x="150" y="92" textAnchor="middle" className="text-[10px]" fill="#94a3b8">直到最优</text>
            </svg>
          </div>
          
          {/* 详细说明 */}
          <div className="flex flex-wrap justify-center gap-4 max-w-2xl mx-auto">
            {[
              { title: '探索', desc: '搜索问题空间' },
              { title: '验证', desc: '确保准确性' },
              { title: '优化', desc: '持续改进' },
              { title: '迭代', desc: '循环收敛' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-100">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-xs">
                  {i + 1}
                </div>
                <span className="text-sm font-medium text-slate-700">{item.title}</span>
                <span className="text-sm text-slate-400">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 用户评价 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10 text-slate-800">用户评价</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reviews.map((review, index) => (
              <div
                key={index}
                className="p-5 bg-white rounded-xl border border-slate-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-medium text-slate-800">{review.name}</div>
                    <div className="text-sm text-slate-400">{review.role}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={12} className="text-violet-500 fill-violet-500" />
                    ))}
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{review.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== Download Section ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-semibold mb-4 text-slate-800">Teegal Workspace</h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://bb-1storage.oss-cn-hangzhou.aliyuncs.com/downloads/teegal-setup.exe"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 rounded-xl text-white transition-colors"
            >
              <Download size={18} />
              <span>Windows</span>
            </a>
            <a
              href="https://bb-1storage.oss-cn-hangzhou.aliyuncs.com/downloads/Teegal-mac.dmg"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 rounded-xl text-white transition-colors"
            >
              <Download size={18} />
              <span>macOS</span>
            </a>
          </div>
        </div>
      </section>

      {/* 循环流动动画样式 */}
      <style>{`
        .flow-path {
          animation: flowDash 2s linear infinite;
        }
        @keyframes flowDash {
          to {
            stroke-dashoffset: -220;
          }
        }
        .flow-dot {
          animation: dotPulse 1.5s ease-in-out infinite;
        }
        @keyframes dotPulse {
          0%, 100% { r: 3; opacity: 1; }
          50% { r: 5; opacity: 0.7; }
        }
        .flow-node {
          animation: nodeGlow 2s ease-in-out infinite;
        }
        @keyframes nodeGlow {
          0%, 100% { stroke-width: 2; }
          50% { stroke-width: 3; }
        }
      `}</style>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-slate-400">
            © 2026 workbees.space. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
